import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Learner } from '@tcm/types';
import { EmptyState, ErrorState, Skeleton } from '@/components/ui';
import { fetchChildren, learningKeys } from '@/features/learning/api';
import { useStoredState } from '@/lib/hooks';

interface ChildContextValue {
  parentName: string;
  children: readonly Learner[];
  child: Learner;
  select: (id: string) => void;
}

const ChildContext = createContext<ChildContextValue | null>(null);

/** Parents with more than one child switch between them from the top bar; every page reads the selected child here. */
export function ChildProvider({ children: content }: { children: ReactNode }) {
  const query = useQuery({ queryKey: learningKeys.children, queryFn: ({ signal }) => fetchChildren(signal) });
  const [selectedId, setSelectedId] = useStoredState<string>('tcm.parent.child', '');

  const value = useMemo<ChildContextValue | null>(() => {
    const list = query.data?.children ?? [];
    const child = list.find((entry) => entry.id === selectedId) ?? list[0];
    return child && query.data ? { parentName: query.data.parentName, children: list, child, select: setSelectedId } : null;
  }, [query.data, selectedId, setSelectedId]);

  if (query.isPending) {
    return <Skeleton className="h-40 w-full" />;
  }
  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (!value) {
    return <EmptyState title="No child is linked to this number" description="Ask the center office to add your mobile number to your child's admission record." />;
  }
  return <ChildContext.Provider value={value}>{content}</ChildContext.Provider>;
}

export function useChild(): ChildContextValue {
  const value = useContext(ChildContext);
  if (!value) {
    throw new Error('useChild must be used inside the parent layout');
  }
  return value;
}
